import { pure } from '@core/helpers/pure';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  Renderer2,
  signal,
} from '@angular/core';
import { AsyncPipe, CommonModule } from '@angular/common';
import { TuiAccordion, TuiSkeleton } from '@taiga-ui/kit';
import {
  FileDownloadService,
  IsLoadingService,
  XAlertService,
} from '@core/services';
import { OutkeepPermissionService } from '@core/services/permission/outkeep-permission.service';
import { RequestFilesService } from '@core/services/request/request-files.service';
import { RequestService } from '@core/services/request/request.service';
import { RequestSettingsService } from '@core/services/request/request-settings.service';
import { TuiButton, TuiHint } from '@taiga-ui/core';
import { environment } from '@environment/environment';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type {
  RequestFileCategory,
  RequestFormFilesDownloadTranslationsPayload,
  TemplateOfActs,
} from '@core/types';
import {
  DenyActTypeEnum,
  RequestFormFilesDownloadTranslations,
} from '@core/types';
import type { Nullable } from '@core/helpers';
import { BehaviorSubject, EMPTY, finalize, Observable } from 'rxjs';
import type {
  CounterpartiesChecklistsCaption,
  FileLink,
} from '@features/requests/request-form/components/request-form-files/types';
import { HttpHeaders } from '@angular/common/http';
import { catchError, filter, switchMap } from 'rxjs/operators';
import { RequestFilesSendByEmailComponent } from './components/request-files-send-by-email';
import { TranslatePipe } from '@ngx-translate/core';
import { OutkeepTranslateService } from '@core/services/outkeep-translate.service';
import { PdfViewerService } from '@core/services/pdf-viewer.service';
import { OutkeepPermission } from '@outkeep/shared';

const { apiUrl } = environment;

@Component({
  selector: 'x-request-form-files-download',
  imports: [
    TuiSkeleton,
    CommonModule,
    AsyncPipe,
    TuiAccordion,
    TuiHint,
    TuiButton,
    RequestFilesSendByEmailComponent,
    TranslatePipe,
  ],
  templateUrl: './request-form-files-download.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestFormFilesDownloadComponent implements OnInit {
  $isInsideSummary = input<boolean>(false, {
    alias: 'isInsideSummary',
  });

  $fileCatsInput = input<Nullable<RequestFileCategory[]>>(null, {
    alias: 'fileCats',
  });

  readonly permissionService = inject(OutkeepPermissionService);
  private readonly requestService = inject(RequestService);
  private readonly requestFilesService = inject(RequestFilesService);
  private readonly requestSettingsService = inject(RequestSettingsService);
  private readonly renderer: Renderer2 = inject(Renderer2);
  private readonly xAlertService = inject(XAlertService);
  private readonly outkeepTranslateService = inject(OutkeepTranslateService);
  private readonly fileDownloadService = inject(FileDownloadService);
  private readonly pdfViewerService = inject(PdfViewerService);

  private translates: RequestFormFilesDownloadTranslationsPayload = {
    [RequestFormFilesDownloadTranslations.ChecklistNotFound]:
      'Чек-лист не был загружен!',
    [RequestFormFilesDownloadTranslations.GeneratedFor]: 'Сгенерированные для ',
  };

  private readonly destroyRef = inject(DestroyRef);
  public isInitialLoad = true;
  readonly isLoading$: BehaviorSubject<boolean> = inject(IsLoadingService);

  private readonly $request = this.requestService.$request;

  private readonly $loadedFileCats = signal<RequestFileCategory[]>([]);

  readonly $fileCats = computed(
    () => this.$fileCatsInput() ?? this.$loadedFileCats(),
  );

  readonly $createdViaApiFileCats = computed(() => {
    return this.$fileCats()?.filter(it =>
      this.fileCatsCreatedWithApiIsShown(it),
    );
  });

  readonly fileCatGroups$ = this.requestService.request$.pipe(
    filter(request => !!request),
    switchMap(request =>
      this.requestFilesService.getRequestFileCategoryGroups$([
        request.counterpartyOwnerId.id,
      ]),
    ),
    takeUntilDestroyed(),
  );

  constructor() {
    effect(onCleanup => {
      if (this.$isInsideSummary() || this.$fileCatsInput() != null) {
        return;
      }

      const subscription = this.requestService.request$
        .pipe(
          filter(request => !!request),
          switchMap(request =>
            this.requestFilesService.getRequestFileCategories$([
              request.counterpartyOwnerId.id,
            ]),
          ),
        )
        .subscribe(fileCats => this.$loadedFileCats.set(fileCats));

      onCleanup(() => subscription.unsubscribe());
    });
  }

  readonly checkList$: Observable<CounterpartiesChecklistsCaption | object> =
    this.requestSettingsService.getCheckList$(
      this.$request().counterpartyOwnerId.id,
    );
  public templatesOfActs: {
    id: TemplateOfActs['id'];
    name: TemplateOfActs['name'];
    click: (templateId: number, templateName: string) => any;
  }[] = [];

  public isGenerating = signal(false);
  public readonly isOpeningRequestPdf = signal(false);

  public getFileCatGroupDownloadLink(categoryGroupId: number): string {
    return this.requestFilesService.getFileCatGroupDownloadLink(
      this.$request().id,
      categoryGroupId,
    );
  }

  private readonly canViewTracker = this.permissionService.$hasPermission(
    OutkeepPermission.LogistViewAnketaTrekera,
  );

  public readonly requestLinks = computed(() => {
    return [
      {
        name: 'Открыть PDF',
        click: this.openRequestPdf.bind(this),
        langId: 'open_pdf',
      },
      {
        name: 'Скачать PDF',
        href: `${apiUrl}/logistic/request/${this.$request().id}/pdf?download=1`,
        // Явное имя с расширением — из URL получилось бы просто "pdf" без расширения
        filename: `request-${this.$request().id}.pdf`,
        langId: 'download_pdf',
      },
      this.canViewTracker()
        ? {
            name: 'Анкета трекера',
            href: `${apiUrl}/logistic/request/${this.$request().id}/anketa_trekera?download=1`,
            // Бэкенд генерирует Excel через exportAnketaTrekeraExToExcel — формат XLSX
            filename: `anketa_trekera-${this.$request().id}.xlsx`,
            langId: 'tracker_form',
          }
        : undefined,
    ].filter(Boolean) as FileLink[];
  });

  public generateDocsLinks: FileLink[] = [
    {
      name: 'Скачать чек-лист',
      click: this.getCheckList.bind(this),
      langId: 'checklist',
    },
  ];

  public linksWithAlfaLogic: FileLink[] = [
    {
      name: 'Скачать акт о неявке ЛП',
      click: this.downloadDenyAct.bind(this, DenyActTypeEnum.NO_LESSEE),
      langId: 'act_no_lessee',
    },
    {
      name: 'Скачать акт отказа',
      click: this.downloadDenyAct.bind(this, DenyActTypeEnum.LESSEE_DENY),
      langId: 'act_lessee_deny',
    },
    {
      name: 'Скачать акт о несостоявшейся отгрузке',
      click: this.downloadDenyAct.bind(this, DenyActTypeEnum.NO_RELEASE),
      langId: 'act_no_release',
    },
    {
      name: 'Скачать акт осмотра',
      click: this.downloadDenyAct.bind(this, DenyActTypeEnum.CAR_REVIEW),
      langId: 'act_car_review',
    },
  ];

  ngOnInit(): void {
    this.loadTemplatesOfActs();
    this.getTranslatedText();
  }

  private getTranslatedText() {
    this.outkeepTranslateService
      .translate(Object.values(RequestFormFilesDownloadTranslations))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(translates => (this.translates = translates));
  }

  @pure
  fileCatsCreatedWithApiIsShown(cat: RequestFileCategory): boolean {
    const req = this.$request();
    const validPrefix = 'obrz_rekvizit';
    const validOwnerIds = [222, 679];

    return (
      !!req.createdViaApi &&
      validPrefix === cat.fileRenamePrefix &&
      validOwnerIds.includes(req.counterpartyOwnerId.id)
    );
  }

  resolveFileCatCreatedWithApi(cat: RequestFileCategory): FileLink | null {
    const reqId = this.$request().id;
    const apiUrl = environment.apiUrl;

    const links = {
      obrz_rekvizit: `${apiUrl}/logistic/request/${reqId}/generateRequisits`,
    };

    if (cat.fileRenamePrefix in links) {
      return {
        name: cat.name[0].toLowerCase() + cat.name.slice(1),
        href: links[cat.fileRenamePrefix as keyof typeof links],
        langId: 'created_with_api',
      };
    }

    return null;
  }

  private getCheckList(e: Event) {
    e.preventDefault();
    this.checkList$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(response => {
        if ('fullPath' in response && response.fullPath) {
          const filename = response.fileName || 'checklist';

          if (this.fileDownloadService.isPdfFile(filename, response.fullPath)) {
            this.fileDownloadService.openPdfByUrl(response.fullPath, filename);

            return;
          }

          this.fileDownloadService.downloadFile(
            response.fullPath,
            filename,
            false,
            this.renderer,
          );
        } else {
          this.xAlertService.error(
            this.translates[
              RequestFormFilesDownloadTranslations.ChecklistNotFound
            ],
          );
        }
      });
  }

  private downloadDenyAct(actType: DenyActTypeEnum) {
    this.requestFilesService
      .downloadDenyAct$(this.$request().id, actType)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ blob, filename }) => {
        const url = URL.createObjectURL(blob);

        this.fileDownloadService.downloadFile(
          url,
          filename,
          false,
          this.renderer,
        );
        // Освобождаем blob: URL после того как браузер/Share Sheet успел его использовать
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      });
  }

  private loadTemplatesOfActs() {
    const requestNumber = this.$request().requestNumber;

    this.requestFilesService
      .loadTemplatesOfActs$(requestNumber)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(payload => {
        this.templatesOfActs = payload.templatesOfActs.map(template => ({
          id: template.id,
          name: template.name,
          click: this.generateAct.bind(this, template.id, template.name),
        }));
        this.isInitialLoad = false;
      });
  }

  private generateAct(templateId: number, templateName: string) {
    if (this.isGenerating()) {
      return;
    }
    this.isGenerating.set(true);

    this.requestFilesService
      .generateAct$(this.$request().requestNumber, templateId)
      .pipe(
        catchError(() => {
          this.isGenerating.set(false);

          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ body }: { body: Blob | null }) => {
        if (body) {
          const url = URL.createObjectURL(
            new Blob([body], {
              type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            }),
          );

          // Добавляем .docx — templateName это название шаблона без расширения,
          // без него iOS не распознаёт формат файла в Share Sheet
          this.createLinkAndClick(url, `${templateName}.docx`);
        }
        this.isGenerating.set(false);
      });
  }

  public generateAllActs() {
    if (this.isGenerating()) {
      return;
    }

    this.isGenerating.set(true);
    this.requestFilesService
      .generateAllActs$(this.$request().requestNumber)
      .pipe(
        catchError(() => {
          this.isGenerating.set(false);

          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(
        ({ headers, body }: { headers: HttpHeaders; body: Blob | null }) => {
          if (body) {
            const disposition = headers.get('content-disposition');
            const filename = disposition
              ? decodeURI(disposition.split(';')[1].trim().split('=')[1])
              : `${
                  this.translates[
                    RequestFormFilesDownloadTranslations.GeneratedFor
                  ]
                }${this.$request().requestNumber}.zip`;
            const blob = new Blob([body], { type: 'application/zip' });

            this.createLinkAndClick(URL.createObjectURL(blob), filename);
          }
          this.isGenerating.set(false);
        },
      );
  }

  openRequestPdf(e: Event) {
    e.preventDefault();

    if (this.isOpeningRequestPdf()) {
      return;
    }

    this.isOpeningRequestPdf.set(true);

    this.requestFilesService
      .openRequestPdf$(this.$request().id)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isOpeningRequestPdf.set(false)),
      )
      .subscribe(({ body }: { body: Blob | null }) => {
        if (body) {
          const blob = new Blob([body], { type: 'application/pdf' });
          const filename = `request-${this.$request().id}.pdf`;

          this.pdfViewerService.open({ source: blob, filename });
        }
      });
  }

  public isRequestLinkDisabled(link: FileLink): boolean {
    return link.langId === 'open_pdf' && this.isOpeningRequestPdf();
  }

  private createLinkAndClick(href: string, filename: string): void {
    this.fileDownloadService.downloadFile(href, filename, false, this.renderer);
  }

  /**
   * Обработчик клика по ссылке с href.
   *
   * В PWA нативная навигация уводит пользователя из приложения — вместо этого
   * файл скачивается через Blob. В обычном браузере поведение не меняется.
   * Логику выбора стратегии делегируем в FileDownloadService.
   *
   * @param event    Событие клика.
   * @param href     URL файла.
   * @param filename Явное имя файла с расширением (из FileLink.filename).
   *                 Если не передано — берётся из последнего сегмента URL.
   *                 Явное имя имеет приоритет: URL вида `/pdf?download=1`
   *                 даст имя "pdf" без расширения, что ломает iOS Share Sheet.
   */
  public handleFileLinkClick(
    event: Event,
    href: string,
    filename?: string,
  ): void {
    event.preventDefault();
    // Явный filename приоритетнее — имя из URL может быть без расширения
    const resolvedFilename =
      filename ?? href.split('/').pop()?.split('?')[0] ?? 'file';

    this.fileDownloadService.downloadFile(
      href,
      resolvedFilename,
      false,
      this.renderer,
    );
  }
}
